import { FC } from "react";
import {
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    InputNumber,
    Row,
    Select,
    Space,
    Typography,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment";

import {
    DealStructuredOutput,
    FlexibleAnalysisOutput,
    NegotiationStatus,
    Project,
    ProjectType,
    RfxStatus,
    RfxStructuredOutput,
    StructuredOutput,
    StructuredOutputType,
} from "../../../../types/ProjectTypes";
import { VendorInput } from "../../../vendor/VendorInput";
import { NegotiationStatusSelect } from "./NegotiationStatusSelect";
import { RfxStatusSelect } from "./RfxStatusSelect";
import { RfxVendorPicker } from "./RfxVendorPicker";

const { Text } = Typography;

interface FlexibleStructuredOutputsTabProps {
    output: FlexibleAnalysisOutput;
    onChange: (output: FlexibleAnalysisOutput) => void;
    project: Project;
}

/**
 * A deal structured output prefilled from the project's linked order form when
 * there is one, so the ops person edits current deal data instead of retyping
 * it. The owner defaults to the project owner for brand-new deals.
 */
export function createDealStructuredOutput(project: Project): DealStructuredOutput {
    const orderForm = project.orderForm;
    return {
        type: StructuredOutputType.DEAL,
        data: {
            dealInfo: orderForm
                ? {
                      vendorKey: orderForm.vendorKey,
                      value: Math.round(orderForm.value * 100),
                      orderStartDate: orderForm.orderStartDate?.toString(),
                      orderEndDate: orderForm.orderEndDate?.toString(),
                      orderScore: orderForm.orderScore,
                      optimizedScore: orderForm.optimizedScore,
                      autoRenewal: orderForm.autoRenewal,
                      status: orderForm.negotiationStatus,
                      ownerId: orderForm.ownerId ?? project.ownerId,
                      increase:
                          orderForm.increase != null
                              ? Math.round(orderForm.increase * 100)
                              : undefined,
                      targetedSavings:
                          orderForm.targetedSavings != null
                              ? Number(orderForm.targetedSavings)
                              : undefined,
                      realizedSavings:
                          orderForm.realizedSavings != null
                              ? Number(orderForm.realizedSavings)
                              : undefined,
                  }
                : {
                      vendorKey: "",
                      value: 0,
                      autoRenewal: false,
                      orderScore: 0,
                      optimizedScore: 0,
                      ownerId: project.ownerId,
                  },
        },
    };
}

/**
 * An RFx structured output prefilled from the project's RFx when it already
 * has one (its candidates and status), so a v2+ output starts from the current
 * RFx instead of an empty one.
 */
export function createRfxStructuredOutput(project: Project): RfxStructuredOutput {
    const rfx = project.rfx;
    return {
        type: StructuredOutputType.RFX,
        data: {
            vendors: (rfx?.vendors ?? []).map(({ vendorKey }) => ({ vendorKey })),
            status: rfx?.status ?? RfxStatus.IN_PROGRESS,
        },
    };
}

// ── RFx structured output form ──────────────────────────────────────

interface RfxStructuredOutputFormProps {
    structuredOutput: RfxStructuredOutput;
    onChange: (updated: RfxStructuredOutput) => void;
    /** The RFx status today, when the project already has an RFx. */
    currentStatus?: RfxStatus;
}

const RfxStructuredOutputForm: FC<RfxStructuredOutputFormProps> = ({
    structuredOutput,
    onChange,
    currentStatus,
}) => {
    const { data } = structuredOutput;
    const keys = data.vendors.map((v) => v.vendorKey);
    const updateData = (patch: Partial<typeof data>) =>
        onChange({ ...structuredOutput, data: { ...data, ...patch } });

    return (
        <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} layout="horizontal" size="small">
            <Form.Item label="Candidate vendors" required>
                <RfxVendorPicker
                    value={keys}
                    onChange={(nextKeys) =>
                        updateData({ vendors: nextKeys.map((vendorKey) => ({ vendorKey })) })
                    }
                />
            </Form.Item>
            <Form.Item label="Status">
                <RfxStatusSelect
                    value={data.status}
                    current={currentStatus}
                    onChange={(status) => updateData({ status })}
                />
            </Form.Item>
        </Form>
    );
};

// ── Dollar amount input (value stored in USD cents) ──────────────────

interface DollarInputProps {
    valueCents: number | undefined;
    onChange: (cents: number | undefined) => void;
}

const DollarInput: FC<DollarInputProps> = ({ valueCents, onChange }) => (
    <InputNumber
        style={{ width: "100%" }}
        min={0}
        value={valueCents != null ? valueCents / 100 : undefined}
        onChange={(v) => onChange(typeof v === "number" ? Math.round(v * 100) : undefined)}
        formatter={(v) => (v == null ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
        parser={(v) => (v ? Number(v.replace(/,/g, "")) : ("" as unknown as number))}
    />
);

// ── Score input (0 to 100) ──────────────────────────────────────────

interface ScoreInputProps {
    value: number;
    onChange: (score: number) => void;
}

const ScoreInput: FC<ScoreInputProps> = ({ value, onChange }) => (
    <InputNumber
        style={{ width: "100%" }}
        min={0}
        max={100}
        value={value}
        onChange={(v) => onChange(typeof v === "number" ? v : 0)}
    />
);

// ── Deal structured output form ──────────────────────────────────────

interface DealStructuredOutputFormProps {
    structuredOutput: DealStructuredOutput;
    onChange: (updated: DealStructuredOutput) => void;
    /** The deal's negotiation status today, when the project already has a deal. */
    currentNegotiationStatus?: NegotiationStatus;
}

const DealStructuredOutputForm: FC<DealStructuredOutputFormProps> = ({
    structuredOutput,
    onChange,
    currentNegotiationStatus,
}) => {
    const { data } = structuredOutput;
    const dealInfo = data.dealInfo ?? {
        vendorKey: "",
        value: 0,
        autoRenewal: false,
        orderScore: 0,
        optimizedScore: 0,
    };

    const updateData = (patch: Partial<typeof data>) => {
        onChange({ ...structuredOutput, data: { ...data, ...patch } });
    };

    const updateDealInfo = (patch: Partial<typeof dealInfo>) => {
        updateData({ dealInfo: { ...dealInfo, ...patch } });
    };

    const hint = (text: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
            {text}
        </Text>
    );

    return (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} layout="horizontal" size="small">
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item label="Vendor" required>
                            <VendorInput
                                key={dealInfo.vendorKey || "empty"}
                                value={dealInfo.vendorKey}
                                onSelect={(vendorKey) => updateDealInfo({ vendorKey })}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Baseline TCV ($)"
                            extra={hint(
                                "The contract value before negotiation, which savings are measured against"
                            )}
                            required
                        >
                            <DollarInput
                                valueCents={dealInfo.value || undefined}
                                onChange={(cents) => updateDealInfo({ value: cents ?? 0 })}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Targeted savings ($)"
                            extra={hint(
                                "Total savings we aim for, regardless of what has been realized"
                            )}
                        >
                            <DollarInput
                                valueCents={dealInfo.targetedSavings ?? undefined}
                                onChange={(cents) => updateDealInfo({ targetedSavings: cents })}
                            />
                        </Form.Item>
                        <Form.Item
                            label="Realized savings ($)"
                            extra={hint("Savings realized so far in the negotiation")}
                        >
                            <DollarInput
                                valueCents={dealInfo.realizedSavings}
                                onChange={(cents) => updateDealInfo({ realizedSavings: cents })}
                            />
                        </Form.Item>
                        <Form.Item label="Deal score (%)">
                            <ScoreInput
                                value={dealInfo.orderScore}
                                onChange={(orderScore) => updateDealInfo({ orderScore })}
                            />
                        </Form.Item>
                        <Form.Item label="Optimized score (%)">
                            <ScoreInput
                                value={dealInfo.optimizedScore}
                                onChange={(optimizedScore) => updateDealInfo({ optimizedScore })}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Negotiation status">
                            <NegotiationStatusSelect
                                value={dealInfo.status}
                                current={currentNegotiationStatus}
                                onChange={(status) => updateDealInfo({ status })}
                            />
                        </Form.Item>
                        <Form.Item label="Start date">
                            <DatePicker
                                style={{ width: "100%" }}
                                value={
                                    dealInfo.orderStartDate
                                        ? moment(dealInfo.orderStartDate)
                                        : undefined
                                }
                                onChange={(d) =>
                                    updateDealInfo({ orderStartDate: d?.toISOString() })
                                }
                            />
                        </Form.Item>
                        <Form.Item label="End date">
                            <DatePicker
                                style={{ width: "100%" }}
                                value={
                                    dealInfo.orderEndDate
                                        ? moment(dealInfo.orderEndDate)
                                        : undefined
                                }
                                onChange={(d) => updateDealInfo({ orderEndDate: d?.toISOString() })}
                            />
                        </Form.Item>
                        <Form.Item label="Auto renewal">
                            <Checkbox
                                checked={dealInfo.autoRenewal}
                                onChange={(e) => updateDealInfo({ autoRenewal: e.target.checked })}
                            />
                        </Form.Item>
                        <Form.Item label="Increase ($)">
                            <DollarInput
                                valueCents={dealInfo.increase}
                                onChange={(cents) => updateDealInfo({ increase: cents })}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Space>
    );
};

// ── Main component ───────────────────────────────────────────────────

export const FlexibleStructuredOutputsTab: FC<FlexibleStructuredOutputsTabProps> = ({
    output,
    onChange,
    project,
}) => {
    const structuredOutputs = output.structuredOutputs || [];

    const hasDealInOutput = structuredOutputs.some((so) => so.type === StructuredOutputType.DEAL);
    const hasRfxInOutput = structuredOutputs.some((so) => so.type === StructuredOutputType.RFX);
    const dealDisabled = hasDealInOutput;
    const dealDisabledReason = hasDealInOutput
        ? "Only one deal structured output is allowed per analysis"
        : undefined;
    const rfxDisabled = project.type !== ProjectType.RFX || hasRfxInOutput;
    const rfxDisabledReason =
        project.type !== ProjectType.RFX
            ? "RFx structured output is only valid on RFx projects"
            : hasRfxInOutput
              ? "Only one RFx structured output is allowed per analysis"
              : undefined;

    const updateAt = (index: number, updated: StructuredOutput) => {
        const next = [...structuredOutputs];
        next[index] = updated;
        onChange({ ...output, structuredOutputs: next });
    };

    const removeAt = (index: number) => {
        const next = structuredOutputs.filter((_, i) => i !== index);
        onChange({ ...output, structuredOutputs: next.length > 0 ? next : undefined });
    };

    const add = (type: StructuredOutputType) => {
        switch (type) {
            case StructuredOutputType.DEAL:
                onChange({
                    ...output,
                    structuredOutputs: [...structuredOutputs, createDealStructuredOutput(project)],
                });
                break;
            case StructuredOutputType.RFX:
                onChange({
                    ...output,
                    structuredOutputs: [...structuredOutputs, createRfxStructuredOutput(project)],
                });
                break;
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {structuredOutputs.map((so, index) => (
                <Card
                    key={index}
                    size="small"
                    title={`${
                        so.type === StructuredOutputType.DEAL ? "Deal" : "RFx"
                    } (#${index + 1})`}
                    extra={
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeAt(index)}
                        />
                    }
                >
                    {so.type === StructuredOutputType.DEAL && (
                        <DealStructuredOutputForm
                            structuredOutput={so}
                            onChange={(updated) => updateAt(index, updated)}
                            currentNegotiationStatus={project.orderForm?.negotiationStatus}
                        />
                    )}
                    {so.type === StructuredOutputType.RFX && (
                        <RfxStructuredOutputForm
                            structuredOutput={so}
                            onChange={(updated) => updateAt(index, updated)}
                            currentStatus={project.rfx?.status}
                        />
                    )}
                </Card>
            ))}

            <Select<StructuredOutputType>
                placeholder="Add structured output..."
                style={{ width: 250 }}
                value={undefined}
                onChange={(type) => add(type)}
                options={[
                    {
                        label: dealDisabledReason ? `Deal — ${dealDisabledReason}` : "Deal",
                        value: StructuredOutputType.DEAL,
                        disabled: dealDisabled,
                    },
                    {
                        label: rfxDisabledReason ? `RFx — ${rfxDisabledReason}` : "RFx",
                        value: StructuredOutputType.RFX,
                        disabled: rfxDisabled,
                    },
                ]}
            />
        </Space>
    );
};
